<template>
  <ListPageShell>
    <template #header>
      <div class="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:px-6">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <UserGroupIcon class="h-5 w-5" />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-slate-900">团队日报</h1>
              <p class="mt-0.5 text-sm text-slate-500">查看直属团队日报提交、评价与补充情况。</p>
            </div>
          </div>
          <button v-if="canManageDepartmentRules" type="button" class="btn-secondary inline-flex items-center gap-1.5 self-start sm:self-auto" @click="openDepartmentRuleDrawer">
            <Cog6ToothIcon class="h-4 w-4" />
            部门规则
          </button>
        </div>
      </div>
    </template>

    <div class="space-y-3">
      <section class="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
      <button
        v-for="scope in scopeTabs"
        :key="scope.value"
        type="button"
        class="rounded-lg px-4 py-2 text-sm font-medium transition-colors"
        :class="activeScope === scope.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'"
        @click="switchScope(scope.value)"
      >
        {{ scope.label }}
      </button>
    </section>

    <section class="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
      <button
        v-for="card in summaryCards"
        :key="card.key"
        type="button"
        class="group flex items-start gap-2.5 rounded-lg border bg-white p-3 text-left shadow-sm transition-all duration-200 hover:shadow-md"
        :class="card.active ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200' : 'border-slate-200 hover:border-blue-200'"
        @click="applySummaryFilter(card)"
      >
        <div class="flex h-8 w-8 items-center justify-center rounded-lg shrink-0" :class="card.iconBg">
          <component :is="card.icon" class="h-4 w-4" :class="card.iconColor" />
        </div>
        <div class="min-w-0 flex-1">
          <span class="block text-[0.7rem] font-medium text-slate-500">{{ card.label }}</span>
          <span class="mt-0.5 block text-lg font-bold leading-none tracking-tight text-slate-900">{{ card.value }}</span>
          <span class="mt-0.5 block text-[0.65rem] font-medium" :class="card.tone">{{ card.hint }}</span>
        </div>
      </button>
    </section>

    <div class="list-page-table-panel">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading || statisticsLoading"
        @refresh="loadPage"
      >
        <input v-model="query.reportDate" class="form-input form-control-sm filter-bar-field" type="date" @change="handleSearch">
        <UserSelector
          v-model="selectedEmployee"
          class="form-control-sm filter-bar-field"
          :multiple="false"
          placeholder="选择员工"
          @update:model-value="handleEmployeeChange"
          @confirm="handleEmployeeConfirm"
        />
        <DeptSelector
          v-model="selectedDept"
          class="form-control-sm filter-bar-field"
          :multiple="false"
          placeholder="选择部门"
          @update:model-value="handleDeptChange"
        />
        <template #searchActions>
          <button type="button" class="btn-primary" :disabled="loading" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" :disabled="loading" @click="resetFilters">重置</button>
        </template>
      </PageFilterBar>
      <div class="data-table-shell">
      <div v-if="loading" class="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
        <span class="text-sm text-slate-500">{{ loadingText }}</span>
      </div>
      <div v-else-if="loadError" class="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
          <ExclamationTriangleIcon class="h-6 w-6 text-rose-500" />
        </div>
        <p class="text-sm text-slate-600">{{ loadErrorText }}</p>
        <button type="button" class="btn-secondary mt-1" @click="loadPage">重试</button>
      </div>
      <div v-else-if="filteredRows.length === 0" class="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
          <InboxIcon class="h-6 w-6 text-slate-400" />
        </div>
        <span class="text-sm text-slate-500">{{ emptyText }}</span>
      </div>
      <div v-else class="data-table-scroll">
        <table class="data-table">
          <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left">员工</th>
              <th class="data-table-th text-left">部门</th>
              <th class="data-table-th text-left">计划</th>
              <th class="data-table-th text-left">总结</th>
              <th class="data-table-th text-left">提醒</th>
              <th class="data-table-th text-right">操作</th>
            </tr>
          </thead>
          <tbody class="data-table-body">
            <tr v-for="row in filteredRows" :key="row.reportId || `${row.employeeUserId}-${row.reportDate}`" class="transition-colors hover:bg-slate-50/80">
              <td class="data-table-td">
                <div class="min-w-0">
                  <div class="break-words font-medium text-slate-900">{{ employeeLabel(row) }}</div>
                  <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>{{ row.reportDate || '--' }}</span>
                    <span v-if="row.employeeUserId">ID {{ row.employeeUserId }}</span>
                  </div>
                </div>
              </td>
              <td class="data-table-td text-slate-700">{{ departmentLabel(row) }}</td>
              <td class="data-table-td"><ReportStatusBadge :status="phaseDisplayStatus(row, 'plan')" /></td>
              <td class="data-table-td"><ReportStatusBadge :status="phaseDisplayStatus(row, 'summary')" /></td>
              <td class="data-table-td">
                <div class="flex flex-wrap gap-1">
                  <span v-if="row.planLate || row.summaryLate" class="status-badge bg-amber-50 text-amber-700">迟交</span>
                  <span v-if="row.planMissing || row.summaryMissing" class="status-badge bg-rose-50 text-rose-700">缺交</span>
                  <span v-if="row.awaitingEvaluation" class="status-badge bg-blue-50 text-blue-700">待评价</span>
                  <span v-if="row.supplementRequested" class="status-badge bg-orange-50 text-orange-700">待补充</span>
                  <span v-if="!(row.planLate || row.summaryLate || row.planMissing || row.summaryMissing) && !row.awaitingEvaluation && !row.supplementRequested" class="text-sm text-slate-500">--</span>
                </div>
              </td>
              <td class="data-table-td text-right">
                <button type="button" class="table-action-button is-primary" title="查看日报详情" aria-label="查看日报详情" @click="openDetail(row)">
                  <EyeIcon class="h-4 w-4" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <AppPagination
        v-if="!loading && !loadError"
        :current="pagination.current"
        :size="pagination.size"
        :total="pagination.total"
        :pages="pagination.pages"
        class="pagination-bridge"
        @page-change="handlePageChange"
        @size-change="handleSizeChange"
      />
      </div>
    </div>
    </div>

    <ReportDetailDrawer
      :model-value="drawerVisible"
      :detail="selectedDetail"
      :loading="detailLoading"
      :can-evaluate="canOperateOnDetail"
      :can-request-supplement="detailCanRequestSupplement"
      :can-comment="detailCanComment"
      :evaluation-submitting="evaluationSubmitting"
      :supplement-submitting="supplementSubmitting"
      :operation-submitting="detailOperationSubmitting"
      :supplement-reset-token="supplementResetToken"
      :selected-version="selectedDetailVersion"
      :version-loading="detailVersionLoading"
      :initial-tab="detailInitialTab"
      :focus-comment-id="notificationFocusCommentId"
      @update:model-value="handleDrawerVisibleChange"
      @evaluate="handleEvaluation"
      @request-supplement="handleSupplement"
      @comment="handleComment"
      @select-version="loadDetailVersion"
    />
    <DepartmentRuleDrawer
      :model-value="departmentRuleDrawerVisible"
      @update:model-value="departmentRuleDrawerVisible = $event"
    />
  </ListPageShell>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { EyeIcon, UserGroupIcon, CheckCircleIcon, ClockIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InboxIcon, DocumentCheckIcon, Cog6ToothIcon } from '@heroicons/vue/24/outline'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import UserSelector from '@/components/UserSelector/index.vue'
import DeptSelector from '@/views/system/components/DeptSelector/index.vue'
import {
  commentTeamWorkReport,
  evaluateWorkReport,
  getCopiedToMeWorkReportDetail,
  getCopiedToMeWorkReportPage,
  getCopiedToMeWorkReportStatistics,
  getCopiedToMeWorkReportVersion,
  getTeamWorkReportDetail,
  getTeamWorkReportPage,
  getTeamWorkReportStatistics,
  getWorkReportAccessSummary,
  getTeamWorkReportVersion,
  requestWorkReportSupplement
} from '@/api/workReport'
import { createClientRequestId, getReportOptimisticVersion } from './workReportUtils'
import {
  createWorkReportDetailOperationGuard,
  isSameWorkReport,
  isWorkReportVersionConflict
} from './composables/workReportDetailOperationGuard.mjs'
import {
  createDefaultTeamWorkbenchStatusFilter
} from './composables/workReportTeamWorkbench.mjs'
import DepartmentRuleDrawer from './components/DepartmentRuleDrawer.vue'
import ReportDetailDrawer from './components/ReportDetailDrawer.vue'
import ReportStatusBadge from './components/ReportStatusBadge.vue'

const route = useRoute()
const TEAM_SCOPE = 'team'
const COPIED_SCOPE = 'copied'
const scopeTabs = [
  { value: TEAM_SCOPE, label: '直属团队' },
  { value: COPIED_SCOPE, label: '抄送我的' }
]
const initialStatusFilter = createDefaultTeamWorkbenchStatusFilter()
const activeScope = ref(TEAM_SCOPE)
const query = reactive({
  reportDate: localDate(),
  employeeUserId: '',
  deptId: '',
  planStatus: initialStatusFilter.planStatus,
  summaryStatus: initialStatusFilter.summaryStatus,
  exceptionType: '',
  current: 1,
  size: 10
})
const selectedEmployee = ref(null)
const selectedDept = ref(null)
const rows = ref([])
const statistics = ref(emptyStatistics())
const pagination = reactive({ current: 1, size: 10, total: 0, pages: 1 })
const loading = ref(false)
const statisticsLoading = ref(false)
const loadError = ref(false)
const drawerVisible = ref(false)
const workReportAccessSummary = ref(defaultWorkReportAccessSummary())
const departmentRuleDrawerVisible = ref(false)
const detailLoading = ref(false)
const detailVersionLoading = ref(false)
const selectedDetail = ref(null)
const selectedDetailVersion = ref(null)
const selectedReportId = ref(null)
const detailInitialTab = ref('comparison')
const notificationFocusCommentId = ref('')
const evaluationSubmitting = ref(false)
const supplementSubmitting = ref(false)
const detailOperationSubmitting = ref(false)
const supplementResetToken = ref(0)
const detailOperationGuard = createWorkReportDetailOperationGuard()
let listRequestToken = 0
let detailRequestToken = 0
let detailVersionRequestToken = 0

const canManageDepartmentRules = computed(() => Boolean(workReportAccessSummary.value?.ruleManageVisible))
const detailSummarySubmitted = computed(() =>
  (selectedDetail.value?.statusSummary?.summaryStatus || selectedDetail.value?.summaryStatus) === 'SUBMITTED'
)
const detailCanEvaluate = computed(() => detailSummarySubmitted.value && detailHasAction('EVALUATE'))
const detailCanRequestSupplement = computed(() => detailSummarySubmitted.value && detailHasAction('REQUEST_SUPPLEMENT'))
const detailCanComment = computed(() => detailHasAction('COMMENT'))
const canOperateOnDetail = computed(() => detailCanEvaluate.value)
const filteredRows = computed(() => rows.value.filter(matchesExceptionType))
const loadingText = computed(() => activeScope.value === COPIED_SCOPE ? '正在加载抄送我的日报...' : '正在加载团队日报...')
const loadErrorText = computed(() => activeScope.value === COPIED_SCOPE ? '抄送我的日报加载失败，请稍后重试。' : '团队日报加载失败，请稍后重试。')
const emptyText = computed(() => activeScope.value === COPIED_SCOPE ? '暂无抄送我的日报' : '暂无符合条件的团队日报')
const summaryCards = computed(() => [
  {
    key: 'scopeTotal',
    label: activeScope.value === COPIED_SCOPE ? '抄送我的' : '所辖员工',
    value: scopeTotal(statistics.value),
    hint: '当前范围总数',
    tone: 'text-slate-500',
    icon: UserGroupIcon,
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    filter: { planStatus: '', summaryStatus: '', exceptionType: '' },
    active: !query.planStatus && !query.summaryStatus && !query.exceptionType
  },
  {
    key: 'planSubmitted',
    label: '计划已交',
    value: numberValue(statistics.value.planSubmitted),
    hint: '计划阶段完成',
    tone: 'text-emerald-600',
    icon: CheckCircleIcon,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    filter: { planStatus: 'SUBMITTED', summaryStatus: '', exceptionType: '' },
    active: query.planStatus === 'SUBMITTED' && !query.summaryStatus && !query.exceptionType
  },
  {
    key: 'planUnsubmitted',
    label: '计划未交',
    value: numberValue(statistics.value.planUnsubmitted),
    hint: '计划尚未提交',
    tone: 'text-rose-600',
    icon: ExclamationTriangleIcon,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    filter: { planStatus: 'DRAFT', summaryStatus: '', exceptionType: '' },
    active: query.planStatus === 'DRAFT' && !query.summaryStatus && !query.exceptionType
  },
  {
    key: 'summarySubmitted',
    label: '总结已交',
    value: numberValue(statistics.value.summarySubmitted),
    hint: '总结阶段完成',
    tone: 'text-emerald-600',
    icon: DocumentCheckIcon,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    filter: { planStatus: '', summaryStatus: 'SUBMITTED', exceptionType: '' },
    active: !query.planStatus && query.summaryStatus === 'SUBMITTED' && !query.exceptionType
  },
  {
    key: 'summaryUnsubmitted',
    label: '总结未交',
    value: numberValue(statistics.value.summaryUnsubmitted),
    hint: '总结尚未提交',
    tone: 'text-rose-600',
    icon: ExclamationTriangleIcon,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    filter: { planStatus: '', summaryStatus: 'DRAFT', exceptionType: '' },
    active: !query.planStatus && query.summaryStatus === 'DRAFT' && !query.exceptionType
  },
  {
    key: 'late',
    label: '存在迟交',
    value: numberValue(statistics.value.late),
    hint: '点击筛选迟交',
    tone: 'text-amber-600',
    icon: ClockIcon,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    filter: { planStatus: '', summaryStatus: '', exceptionType: 'LATE' },
    active: query.exceptionType === 'LATE'
  },
  {
    key: 'awaitingEvaluation',
    label: '待评价',
    value: numberValue(statistics.value.awaitingEvaluation),
    hint: '点击筛选待评价',
    tone: 'text-blue-600',
    icon: ExclamationCircleIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    filter: { planStatus: '', summaryStatus: '', exceptionType: 'PENDING_EVALUATION' },
    active: query.exceptionType === 'PENDING_EVALUATION'
  },
  {
    key: 'supplementRequested',
    label: '待补充',
    value: numberValue(statistics.value.supplementRequested),
    hint: '点击筛选待补充',
    tone: 'text-orange-600',
    icon: ExclamationCircleIcon,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    filter: { planStatus: '', summaryStatus: '', exceptionType: 'PENDING_SUPPLEMENT' },
    active: query.exceptionType === 'PENDING_SUPPLEMENT'
  }
])

onMounted(async () => {
  await fetchWorkReportAccessSummary()
  await loadPage()
  await openDetailFromRouteQuery()
})

watch(() => route.query, () => {
  openDetailFromRouteQuery()
})

async function loadPage() {
  const requestToken = ++listRequestToken
  loading.value = true
  statisticsLoading.value = true
  loadError.value = false
  try {
    const [page, scopeStatistics] = await Promise.all([
      activePageApi()(requestParams()),
      loadStatistics()
    ])
    if (requestToken !== listRequestToken) return
    rows.value = pageRows(page)
    statistics.value = normalizeStatistics(scopeStatistics)
    pagination.current = Number(page?.current) || query.current
    pagination.size = Number(page?.size) || query.size
    pagination.total = Number(page?.total) || 0
    pagination.pages = Number(page?.pages) || Math.max(1, Math.ceil(pagination.total / pagination.size) || 1)
  } catch {
    if (requestToken !== listRequestToken) return
    rows.value = []
    statistics.value = emptyStatistics()
    loadError.value = true
  } finally {
    if (requestToken === listRequestToken) {
      loading.value = false
      statisticsLoading.value = false
    }
  }
}

async function loadStatistics() {
  return normalizeStatistics(await activeStatisticsApi()(statisticsRequestParams()))
}

function requestParams() {
  return {
    ...baseRequestParams(),
    planStatus: query.planStatus || undefined,
    summaryStatus: query.summaryStatus || undefined,
    exceptionType: query.exceptionType || undefined,
    current: query.current,
    size: query.size
  }
}

function statisticsRequestParams() {
  return baseRequestParams()
}

function baseRequestParams() {
  return {
    reportDate: query.reportDate || undefined,
    employeeUserId: query.employeeUserId || undefined,
    deptId: query.deptId || undefined
  }
}

function activePageApi() {
  return activeScope.value === COPIED_SCOPE ? getCopiedToMeWorkReportPage : getTeamWorkReportPage
}

function activeStatisticsApi() {
  return activeScope.value === COPIED_SCOPE ? getCopiedToMeWorkReportStatistics : getTeamWorkReportStatistics
}

function activeDetailApi() {
  return activeScope.value === COPIED_SCOPE ? getCopiedToMeWorkReportDetail : getTeamWorkReportDetail
}

function activeVersionApi() {
  return activeScope.value === COPIED_SCOPE ? getCopiedToMeWorkReportVersion : getTeamWorkReportVersion
}

function pageRows(page) {
  if (Array.isArray(page?.records)) return page.records
  if (Array.isArray(page?.list)) return page.list
  if (Array.isArray(page?.items)) return page.items
  return []
}

function emptyStatistics() {
  return {
    employeeTotal: 0,
    copiedToMeTotal: 0,
    planSubmitted: 0,
    planUnsubmitted: 0,
    summarySubmitted: 0,
    summaryUnsubmitted: 0,
    planMissing: 0,
    summaryMissing: 0,
    late: 0,
    awaitingEvaluation: 0,
    supplementRequested: 0
  }
}

function defaultWorkReportAccessSummary() {
  return {
    selfReportVisible: true,
    teamWorkbenchVisible: false,
    ruleManageVisible: false,
    defaultTeamScope: 'TEAM'
  }
}

async function fetchWorkReportAccessSummary() {
  try {
    workReportAccessSummary.value = { ...defaultWorkReportAccessSummary(), ...(await getWorkReportAccessSummary() || {}) }
  } catch (error) {
    console.error('加载日报入口权限失败:', error)
    workReportAccessSummary.value = defaultWorkReportAccessSummary()
  }
}

function normalizeStatistics(value) {
  return { ...emptyStatistics(), ...(value && typeof value === 'object' ? value : {}) }
}

function numberValue(value) {
  return Number(value) || 0
}

function scopeTotal(value) {
  return activeScope.value === COPIED_SCOPE
    ? numberValue(value?.copiedToMeTotal)
    : numberValue(value?.employeeTotal)
}

function detailHasAction(action) {
  const actions = selectedDetail.value?.detailContext?.availableActions
  return Array.isArray(actions) && actions.includes(action)
}

function matchesExceptionType(row) {
  return Boolean(row)
}

function phaseDisplayStatus(row, phase) {
  const missingField = phase === 'plan' ? 'planMissing' : 'summaryMissing'
  const lateField = phase === 'plan' ? 'planLate' : 'summaryLate'
  if (row?.[missingField]) return 'MISSING'
  if (row?.[lateField]) return 'LATE'
  return row?.[`${phase}Status`] || 'DRAFT'
}

function handleSearch() {
  query.current = 1
  loadPage()
}

function switchScope(scope) {
  if (scope !== TEAM_SCOPE && scope !== COPIED_SCOPE) return
  if (activeScope.value === scope) return
  activeScope.value = scope
  Object.assign(query, createDefaultTeamWorkbenchStatusFilter())
  query.exceptionType = ''
  query.current = 1
  handleDrawerVisibleChange(false)
  loadPage()
}

function resetFilters() {
  query.reportDate = localDate()
  query.employeeUserId = ''
  query.deptId = ''
  Object.assign(query, createDefaultTeamWorkbenchStatusFilter())
  query.exceptionType = ''
  selectedEmployee.value = null
  selectedDept.value = null
  query.current = 1
  loadPage()
}

function openDepartmentRuleDrawer() {
  if (!canManageDepartmentRules.value) return
  departmentRuleDrawerVisible.value = true
}

function handleEmployeeConfirm(users) {
  const [user] = Array.isArray(users) ? users : []
  selectedEmployee.value = user || null
  query.employeeUserId = normalizeSelectorId(user || selectedEmployee.value)
  handleSearch()
}

function handleEmployeeChange(value) {
  selectedEmployee.value = value
  query.employeeUserId = normalizeSelectorId(value)
  if (!query.employeeUserId) handleSearch()
}

function handleDeptChange(value) {
  selectedDept.value = value
  query.deptId = normalizeSelectorId(value)
  handleSearch()
}

function normalizeSelectorId(value) {
  if (Array.isArray(value)) return normalizeSelectorId(value[0])
  if (value && typeof value === 'object') return value.id ?? ''
  return value || ''
}

function applySummaryFilter(card) {
  if (!card?.filter) return
  Object.assign(query, card.filter)
  handleSearch()
}

function handlePageChange(current) {
  query.current = current
  loadPage()
}

function handleSizeChange(size) {
  query.size = size
  query.current = 1
  loadPage()
}

async function openDetail(row) {
  if (!row?.reportId) {
    ElMessage.warning('该员工当天尚未生成日报，无法打开详情')
    return
  }
  detailInitialTab.value = 'comparison'
  notificationFocusCommentId.value = ''
  selectedReportId.value = row.reportId
  selectedDetail.value = null
  selectedDetailVersion.value = null
  detailVersionRequestToken += 1
  detailVersionLoading.value = false
  drawerVisible.value = true
  await loadDetail(row.reportId)
}

async function openDetailByReportId(reportId) {
  if (!reportId) return
  selectedReportId.value = reportId
  selectedDetail.value = null
  selectedDetailVersion.value = null
  detailVersionRequestToken += 1
  detailVersionLoading.value = false
  drawerVisible.value = true
  await loadDetail(reportId)
}

async function openDetailFromRouteQuery() {
  const routeReportId = normalizeRouteId(route.query.reportId || route.query.id)
  const routeDate = normalizeReportDateQuery(route.query.reportDate)
  const routeScope = normalizeRouteId(route.query.scope)
  const wantsFeedback = isTruthyRouteFlag(route.query.feedback)
  const wantsEvaluation = isTruthyRouteFlag(route.query.evaluation)
  const wantsOpenRules = isTruthyRouteFlag(route.query.openRules)

  notificationFocusCommentId.value = normalizeRouteId(route.query.commentId)
  detailInitialTab.value = wantsEvaluation ? 'evaluation' : wantsFeedback ? 'comments' : 'comparison'
  if (routeDate) query.reportDate = routeDate
  if (routeScope === COPIED_SCOPE) activeScope.value = COPIED_SCOPE
  else if (routeScope === TEAM_SCOPE) activeScope.value = TEAM_SCOPE
  if (wantsOpenRules) {
    openDepartmentRuleDrawer()
  }
  if (!routeReportId) return
  await openDetailByReportId(routeReportId)
}

async function loadDetail(reportId) {
  const requestToken = ++detailRequestToken
  detailLoading.value = true
  try {
    const detail = await activeDetailApi()(reportId)
    if (requestToken !== detailRequestToken || !isCurrentSelectedReport(reportId)) return
    selectedDetail.value = detail
  } catch {
    if (requestToken !== detailRequestToken || !isCurrentSelectedReport(reportId)) return
    ElMessage.error('日报详情加载失败，请稍后重试')
    handleDrawerVisibleChange(false)
  } finally {
    if (requestToken === detailRequestToken && isCurrentSelectedReport(reportId)) detailLoading.value = false
  }
}

function handleDrawerVisibleChange(visible) {
  drawerVisible.value = visible
  if (!visible) {
    selectedReportId.value = null
    selectedDetail.value = null
    selectedDetailVersion.value = null
    detailRequestToken += 1
    detailVersionRequestToken += 1
    detailLoading.value = false
    detailVersionLoading.value = false
  }
}

function isCurrentSelectedReport(reportId) {
  return drawerVisible.value && isSameWorkReport(reportId, selectedReportId.value)
}

async function refreshDetailIfSelected(reportId) {
  if (isCurrentSelectedReport(reportId)) {
    selectedDetailVersion.value = null
    detailVersionRequestToken += 1
    detailVersionLoading.value = false
    await loadDetail(reportId)
  }
}

async function loadDetailVersion(version) {
  const reportId = selectedDetail.value?.reportId ?? selectedDetail.value?.id ?? selectedReportId.value
  if (!reportId || !version?.phase || !version?.versionNo) {
    selectedDetailVersion.value = null
    return
  }

  const requestToken = ++detailVersionRequestToken
  detailVersionLoading.value = true
  try {
    const snapshot = await activeVersionApi()(reportId, version.phase, version.versionNo)
    if (requestToken !== detailVersionRequestToken || !isCurrentSelectedReport(reportId)) return
    selectedDetailVersion.value = snapshot
  } catch {
    if (requestToken !== detailVersionRequestToken || !isCurrentSelectedReport(reportId)) return
    ElMessage.error('版本内容加载失败，请稍后重试')
  } finally {
    if (requestToken === detailVersionRequestToken && isCurrentSelectedReport(reportId)) {
      detailVersionLoading.value = false
    }
  }
}

function beginDetailOperation(phase, action = 'EVALUATE') {
  const detail = selectedDetail.value
  const reportId = detail?.reportId ?? detail?.id
  if (!reportId || !detailHasAction(action)) return null

  const operation = detailOperationGuard.acquire(reportId, phase)
  if (!operation) return null

  detailOperationSubmitting.value = true
  return { detail, operation }
}

function finishDetailOperation(operation) {
  detailOperationGuard.release(operation)
  detailOperationSubmitting.value = detailOperationGuard.isLocked()
}

async function handleDetailOperationFailure(error, reportId, fallbackMessage) {
  if (isWorkReportVersionConflict(error)) {
    ElMessage.warning('当前日报已被其他操作更新，正在刷新最新详情')
    await refreshDetailIfSelected(reportId)
    return
  }

  ElMessage.error(fallbackMessage)
}

async function handleEvaluation(evaluation) {
  const started = beginDetailOperation('SUMMARY', 'EVALUATE')
  if (!started) return

  const { detail, operation } = started

  evaluationSubmitting.value = true
  try {
    await evaluateWorkReport(operation.reportId, {
      phase: 'SUMMARY',
      grade: evaluation.grade,
      comment: evaluation.comment,
      expectedVersion: getReportOptimisticVersion(detail),
      clientRequestId: createClientRequestId()
    })
    ElMessage.success('评价已提交')
    await refreshDetailIfSelected(operation.reportId)
    await loadPage()
  } catch (error) {
    await handleDetailOperationFailure(error, operation.reportId, '评价提交失败，请稍后重试')
  } finally {
    evaluationSubmitting.value = false
    finishDetailOperation(operation)
  }
}

async function handleSupplement(supplement) {
  if (!supplement?.content) return

  const started = beginDetailOperation('SUMMARY', 'REQUEST_SUPPLEMENT')
  if (!started) return

  const { detail, operation } = started

  supplementSubmitting.value = true
  try {
    await requestWorkReportSupplement(operation.reportId, {
      content: supplement.content,
      expectedVersion: getReportOptimisticVersion(detail),
      clientRequestId: createClientRequestId()
    })
    ElMessage.success('已请求补充')
    supplementResetToken.value += 1
    await refreshDetailIfSelected(operation.reportId)
    await loadPage()
  } catch (error) {
    await handleDetailOperationFailure(error, operation.reportId, '请求补充失败，请稍后重试')
  } finally {
    supplementSubmitting.value = false
    finishDetailOperation(operation)
  }
}

async function handleComment(comment) {
  if (!comment?.content || !selectedDetail.value) return
  if (!detailCanComment.value) return

  const reportId = selectedDetail.value.reportId ?? selectedDetail.value.id
  if (!reportId) return

  detailOperationSubmitting.value = true
  try {
    await commentTeamWorkReport(reportId, {
      parentCommentId: comment.parentCommentId || null,
      content: comment.content,
      clientRequestId: createClientRequestId()
    })
    ElMessage.success('评论已提交')
    await refreshDetailIfSelected(reportId)
  } catch {
    ElMessage.error('评论提交失败，请稍后重试')
  } finally {
    detailOperationSubmitting.value = detailOperationGuard.isLocked()
  }
}

function employeeLabel(row) {
  if (typeof row?.employee === 'string') return row.employee
  return row?.employee?.name || row?.employee?.realName || row?.employeeName || '--'
}

function departmentLabel(row) {
  if (typeof row?.dept === 'string') return row.dept
  return row?.dept?.name || row?.deptName || '--'
}

function localDate() {
  const now = new Date()
  const offset = now.getTimezoneOffset()
  return new Date(now.getTime() - offset * 60 * 1000).toISOString().slice(0, 10)
}

function normalizeRouteId(value) {
  const text = Array.isArray(value) ? value[0] : value
  return text === null || text === undefined || text === '' ? '' : String(text)
}

function normalizeReportDateQuery(value) {
  const text = Array.isArray(value) ? value[0] : value
  return /^\d{4}-\d{2}-\d{2}$/.test(String(text || '')) ? text : ''
}

function isTruthyRouteFlag(value) {
  const text = Array.isArray(value) ? value[0] : value
  return text === true || text === '1' || text === 'true'
}
</script>
